import { Fragment, useCallback, useMemo } from 'react';
import Typography from '@mui/material/Typography';
import Link from 'next/link';
import { makeStyles } from 'tss-react/mui';
import Alert from '@mui/material/Alert';
import Tabs, { type TabOption } from '../tabs';
import MaterialLinkButton from '../material-link-button';
import EventHeaderTags from './header-tags';
import { eventPath } from '../../utils/slugify';
import type { Event } from '../../types/Event';

const useStyles = makeStyles()((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(2, 0, 2, 0),
    [theme.breakpoints.down('sm')]: {
      flexFlow: 'column',
      alignItems: 'flex-start',
      marginBottom: theme.spacing(2),
    },
  },
  headerLeft: {
    flexGrow: 1,
    paddingRight: theme.spacing(2),
  },
  title: {
    margin: 0,
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  iconChip: {
    maxWidth: '100%',
    '& > span': {
      display: 'inline-flex',
      alignItems: 'center',
    },
  },
  icon: {
    marginRight: theme.spacing(0.5),
    fontSize: 16,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      flexFlow: 'column',
      alignItems: 'flex-start',
      maxWidth: '100%',
      width: '100%',
      marginTop: theme.spacing(1),
    },
  },
  titleLink: {
    color: 'rgba(0, 0, 0, 0.87)',
    textDecoration: 'none',
  },
  cancelledTitle: {
    textDecoration: 'line-through',
  },
}));

type Props = {
  event?: Event;
  tab?: string;
  noTabs?: boolean;
  hideDraftAlert?: boolean;
};

const EventHeader = (props: Props) => {
  const { event, tab, noTabs = false, hideDraftAlert = false } = props;
  const { classes, cx } = useStyles();

  const handleChangeTab = useCallback((e, newTab) => {
    if (newTab === 'overview') {
      e.preventDefault();
      window.scrollTo(0, 0);
    }
    // else if (newTab === 'discussion') {
    //   document.getElementById('discussion').scrollIntoView();
    // }
  }, []);

  const tabOptions = useMemo(() => {
    if (noTabs) {
      return [];
    }

    const options: TabOption[] = [
      {
        label: 'Overview',
        value: 'overview',
      },
    ];
    if (event.permissions.approved) {
      options.push({
        label: `Discussion (${event.imageUploadsCount + event.commentCount})`,
        value: 'discussion',
        component: 'a',
        href: '#discussion'
      });
    }
    return options;
  }, [event, noTabs]);

  return (
    <Fragment>
      {!hideDraftAlert && event.status === 'DRAFT' && event.permissions.editable && (
        <Alert
          severity="warning"
          action={
            <MaterialLinkButton variant="outlined" href={`${eventPath(event.id, event.name)}/edit`}>
              Edit to Publish
            </MaterialLinkButton>
          }
          sx={{ my: 2 }}
        >
          This event is currently unpublished
        </Alert>
      )}
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <Typography variant="h1" className={cx(classes.title, { [classes.cancelledTitle]: event.status === 'CANCELLED' })}>
            <Link
              href={eventPath(event.id, event.name)}
              className={classes.titleLink}
            >
              {event.name}
            </Link>
          </Typography>

          <div className={classes.subtitle}>
            <EventHeaderTags event={event} />
          </div>
        </div>

        <div className={classes.headerRight}>
          {tabOptions.length > 1 && (
            <Tabs
              options={tabOptions}
              tab={tab}

              onTabChange={handleChangeTab}
            />
          )}
        </div>
      </div>
    </Fragment>
  );
}

export default EventHeader;
